import { useCallback, useSyncExternalStore } from 'react'

// Frontend PRD 10. Same storage convention as useAuthToken.ts, extended with
// a cross-tab `storage` listener and a `matchMedia` listener (the latter only
// acts while no explicit choice has been stored - an explicit toggle wins
// over the OS preference forever after, until localStorage is cleared).
export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'legalflow-config-theme'

const listeners = new Set<() => void>()

function getSystemTheme(): Theme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function resolveTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') return stored
  return getSystemTheme()
}

function applyThemeClass(theme: Theme) {
  document.documentElement.classList.toggle('dark', theme === 'dark')
  document.documentElement.classList.toggle('light', theme === 'light')
}

// Initialized from the class already on <html> (set by index.html's blocking
// script before React ever mounts), not recomputed independently - the hook
// and the blocking script must never disagree.
let cachedTheme: Theme = document.documentElement.classList.contains('light') ? 'light' : 'dark'

function getSnapshot(): Theme {
  return cachedTheme
}

function writeTheme(theme: Theme) {
  cachedTheme = theme
  localStorage.setItem(STORAGE_KEY, theme)
  applyThemeClass(theme)
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

// Cross-tab: the native `storage` event fires only in *other* tabs, so an
// already-open second tab repaints live when the operator toggles in a
// first tab (Frontend PRD 10 acceptance criterion 7).
window.addEventListener('storage', (event) => {
  if (event.key !== STORAGE_KEY && event.key !== null) return
  cachedTheme = resolveTheme()
  applyThemeClass(cachedTheme)
  listeners.forEach((listener) => listener())
})

// Live OS-preference tracking, only while no explicit choice has ever been
// stored in this browser profile (acceptance criteria 5 and 6).
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
  if (localStorage.getItem(STORAGE_KEY) !== null) return
  cachedTheme = getSystemTheme()
  applyThemeClass(cachedTheme)
  listeners.forEach((listener) => listener())
})

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot)

  const toggleTheme = useCallback(() => {
    writeTheme(cachedTheme === 'dark' ? 'light' : 'dark')
  }, [])

  return { theme, toggleTheme }
}
